/**
 * PaletteUniforms.ts — shared uniform buffer with the project's color palette.
 *
 * No shader hard-codes a color. Every shader reads from this buffer.
 *
 * The palette is laid out as an array of vec4<f32> (alpha unused) so that
 * WGSL `array<vec4<f32>, N>` indexing works directly without alignment hassle.
 *
 * Indices (TypeScript enum mirrored as WGSL constants in palette.wgsl):
 *   0  SKY_ZENITH
 *   1  SKY_MIDBAND
 *   2  SKY_HORIZON
 *   3  WATER_SHALLOW
 *   4  WATER_MID
 *   5  WATER_DEEP
 *   6  WATER_FOAM
 *   7  LAND_GRASS
 *   8  LAND_CLIFF
 *   9  LAND_BEACH
 *   10 LAND_ROCK
 *   11 WOOD_DARK
 *   12 WOOD_LIGHT
 *   13 WOOD_HIGHLIGHT
 *
 * Total: 14 vec4 = 224 bytes.
 */

import { createUniformBuffer } from "../gpu/Buffers";
import { srgbHexToLinear } from "./LightingUniforms";

export const PALETTE_COUNT = 14;
const STRUCT_SIZE = PALETTE_COUNT * 16;

export const enum PaletteIndex {
  SkyZenith = 0,
  SkyMidband = 1,
  SkyHorizon = 2,
  WaterShallow = 3,
  WaterMid = 4,
  WaterDeep = 5,
  WaterFoam = 6,
  LandGrass = 7,
  LandCliff = 8,
  LandBeach = 9,
  LandRock = 10,
  WoodDark = 11,
  WoodLight = 12,
  WoodHighlight = 13,
}

export interface PaletteState {
  /** Linear RGB colors, one per PaletteIndex. */
  colors: [number, number, number][];
}

/** Default palette from the project brief (sRGB → linear). */
export function defaultPaletteState(): PaletteState {
  const c = (hex: string) => srgbHexToLinear(hex);
  return {
    colors: [
      c("#4A6FA5"), // SkyZenith
      c("#87B2C9"), // SkyMidband
      c("#E8D4B0"), // SkyHorizon
      c("#7AA0B8"), // WaterShallow — muted steel blue
      c("#3F627A"), // WaterMid — deep slate blue
      c("#1F3447"), // WaterDeep — dark navy
      c("#DDE4EA"), // WaterFoam — soft cool white
      c("#5A8B47"), // LandGrass
      c("#826E5A"), // LandCliff
      c("#D4B896"), // LandBeach
      c("#6B6157"), // LandRock
      c("#5C3A28"), // WoodDark
      c("#8B5A3C"), // WoodLight
      c("#C49464"), // WoodHighlight
    ],
  };
}

export class PaletteUniforms {
  readonly buffer: GPUBuffer;
  readonly state: PaletteState;
  private cpu = new ArrayBuffer(STRUCT_SIZE);
  private f32 = new Float32Array(this.cpu);

  constructor(
    private device: GPUDevice,
    state: PaletteState = defaultPaletteState(),
  ) {
    this.buffer = createUniformBuffer(device, STRUCT_SIZE, "PaletteUniforms");
    this.state = state;
    this.upload();
  }

  upload(): void {
    for (let i = 0; i < PALETTE_COUNT; i++) {
      const col = this.state.colors[i] ?? [1, 0, 1];
      this.f32[i * 4 + 0] = col[0];
      this.f32[i * 4 + 1] = col[1];
      this.f32[i * 4 + 2] = col[2];
      this.f32[i * 4 + 3] = 1.0;
    }
    this.device.queue.writeBuffer(this.buffer, 0, this.cpu);
  }

  /** Sky horizon color in linear RGB; convenient for fog default. */
  skyHorizon(): [number, number, number] {
    return this.state.colors[PaletteIndex.SkyHorizon] ?? [1, 1, 1];
  }

  destroy(): void {
    this.buffer.destroy();
  }
}
